import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { STORAGE_KEYS } from '../constants';
import { useAuth } from './AuthContext';

interface AdminModeContextType {
  // Whether the admin inline-edit mode is currently enabled. Always false for
  // non-admin users.
  adminMode: boolean;
  toggleAdminMode: () => void;
  setAdminMode: (value: boolean) => void;
}

const AdminModeContext = createContext<AdminModeContextType | undefined>(undefined);

export const useAdminMode = (): AdminModeContextType => {
  const context = useContext(AdminModeContext);
  if (context === undefined) {
    throw new Error('useAdminMode must be used within an AdminModeProvider');
  }
  return context;
};

interface AdminModeProviderProps {
  children: ReactNode;
}

export const AdminModeProvider: React.FC<AdminModeProviderProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const isAdmin = !!user?.is_admin;

  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEYS.ADMIN_MODE) === '1';
    } catch {
      return false;
    }
  });

  const persist = useCallback((value: boolean) => {
    try {
      if (value) {
        localStorage.setItem(STORAGE_KEYS.ADMIN_MODE, '1');
      } else {
        localStorage.removeItem(STORAGE_KEYS.ADMIN_MODE);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  // Non-admins can never have admin mode on. Reset only once auth has finished
  // loading and the user is confirmed to be a non-admin (e.g. logout or a
  // non-admin account). We must NOT reset while auth is still loading on a page
  // refresh — at that point `user` is briefly null, and clearing `enabled` here
  // would drop the persisted admin mode before the admin user is restored.
  useEffect(() => {
    if (!loading && !isAdmin && enabled) {
      setEnabled(false);
      persist(false);
    }
  }, [loading, isAdmin, enabled, persist]);

  const setAdminMode = useCallback((value: boolean) => {
    const next = value && isAdmin;
    setEnabled(next);
    persist(next);
  }, [isAdmin, persist]);

  const toggleAdminMode = useCallback(() => {
    setAdminMode(!enabled);
  }, [enabled, setAdminMode]);

  const value: AdminModeContextType = {
    adminMode: enabled && isAdmin,
    toggleAdminMode,
    setAdminMode,
  };

  return (
    <AdminModeContext.Provider value={value}>
      {children}
    </AdminModeContext.Provider>
  );
};
